import type { ElementType, ReactNode } from 'react';
import styles from './Text.module.css';

/**
 * **`Text`**（`docs/design-system.md` §3 / §5.1）。
 *
 * ## 型で守っていること
 *
 * **任意サイズの prop を持たせない**（§5.1）— **スケール外の値（15px）が
 * 入る経路を作らない**。`variant` は §3 の表の段そのものである。
 *
 * **`Heading` は下にある**（M16 で `--ua-heading-size` を Layer 2 に足した）。
 */
export interface TextProps {
  /** §3 の表の段。**任意の値は取らない** */
  readonly variant?: 'body' | 'note' | 'oneline' | 'mono' | 'dense';
  /**
   * **数字の桁を揃える**（残メッセージ数 = D29 / コスト内訳 = D26）。
   * **`mono` とは別の軸** — 等幅にしなくても桁は揃えたい場面がある。
   */
  readonly tabular?: boolean;
  /** 既定は `<p>`。1 行 UI では `<span>` にする */
  readonly as?: ElementType;
  readonly children: ReactNode;
}

export function Text({
  variant = 'body',
  tabular = false,
  as: Tag = 'p',
  children,
}: TextProps): ReactNode {
  const classes = [styles[variant], tabular ? styles['tabular'] : undefined]
    .filter(Boolean)
    .join(' ');
  return <Tag className={classes}>{children}</Tag>;
}

/**
 * **`Heading`**（§3 の `Std-24B-140` / §5.1）。
 *
 * **見出しの階層は `level` で表し、サイズは変えない。**
 * §3 は見出しの段を **1 つしか定義していない**ので、
 * **`h1` と `h2` を見た目で区別しない**。
 * **段を勝手に増やさない** — 増やすなら §3 の表に足すのが先である。
 */
export interface HeadingProps {
  /** **文書構造の階層**（見た目ではない） */
  readonly level: 1 | 2 | 3;
  readonly children: ReactNode;
}

export function Heading({ level, children }: HeadingProps): ReactNode {
  const Tag = (`h${level}`) as ElementType;
  return <Tag className={styles['heading']}>{children}</Tag>;
}
